/**
 * The Fidèle (DECISIONS row 61): one cart row per variant.
 *
 * With the gift in the bag, Shopify's Buy X Get Y discount ($135 minimum) tags only the paid units it needs to reach
 * the minimum and splits a line whose units are only partly needed, so 3 pairs raised to 4 come back as two lines of
 * the same variant (1 and 3). `snippets/cart-products.liquid` shows such a group as one row (`data-fdl-group` holds
 * every line key of the group, the row's own key first) and hides the other lines.
 *
 * Here, the stepper and the trash of a grouped row act on the whole group: one /cart/update.js call puts the new
 * total on the first line and 0 on the others (Shopify may split it again; the row still reads one total), then every
 * cart section redraws through the same cart event Horizon uses. Rows of one line keep Horizon's own path.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';
/** @type {Map<string, number>} */
const timers = new Map();

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

/** @param {Element} row */
function groupKeys(row) {
  return (row instanceof HTMLElement ? row.dataset.fdlGroup || '' : '').split(',').filter(Boolean);
}

/**
 * @param {string[]} keys - the group's line keys, the shown line first
 * @param {number} quantity - the group's new total (0 removes every line of the group)
 */
async function setGroup(keys, quantity) {
  if (!keys.length) return;
  /** @type {Record<string, number>} */
  const updates = {};
  keys.forEach((key, i) => {
    updates[key] = i === 0 ? quantity : 0;
  });
  const ids = sectionIds();
  const body = { updates };
  if (ids.length) Object.assign(body, { sections: ids.join(','), sections_url: window.location.pathname });

  const components = document.querySelectorAll('cart-items-component');
  components.forEach((el) => el.classList.add('cart-items-disabled'));
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: quantity > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: keys.map((id, i) => ({ id, quantity: i === 0 ? quantity : 0 })),
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) {
      deferred.reject(new Error(cart.description || cart.message || 'Cart update failed'));
      document.dispatchEvent(new CartErrorEvent({ error: cart.description || 'Cart update failed', code: 'INVALID' }));
      return;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'fidele-cart-lines',
        sourceId: 'fidele-cart-lines',
        sections: cart.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    components.forEach((el) => el.classList.remove('cart-items-disabled'));
  }
}

// The stepper of a grouped row: caught before Horizon's document listener, which would change one line only.
document.addEventListener(
  'quantity-selector:update',
  (event) => {
    const row = event.target instanceof Element ? event.target.closest('[data-fdl-group]') : null;
    if (!row) return;
    event.stopImmediatePropagation();
    const quantity = Number(/** @type {any} */ (event).detail?.quantity);
    const keys = groupKeys(row);
    if (!Number.isFinite(quantity) || quantity < 0 || !keys.length) return;
    window.clearTimeout(timers.get(keys[0]));
    timers.set(
      keys[0],
      window.setTimeout(() => {
        timers.delete(keys[0]);
        setGroup(keys, quantity);
      }, 300)
    );
  },
  true
);

// The trash of a grouped row removes the whole group.
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-fdl-group-remove]') : null;
  if (!button) return;
  const row = button.closest('[data-fdl-group]');
  if (!row) return;
  event.preventDefault();
  setGroup(groupKeys(row), 0);
});
